/**
 * 图片 CDN 处理参数工具（腾讯云数据万象 / imageMogr2）
 *
 * 背景 —— 全站图片都是「直接渲染原图地址，再靠 CSS 缩小显示」，实测：
 *   头像原图      33KB ~ 467KB（平均 176KB），实际只显示 36~40px
 *   帖子配图      268KB（1079x1920），列表里只显示 138px
 *   一页 10 条帖子，光头像就要下 1.8MB
 *
 * pic.ylinhua.com 挂在腾讯云 CDN 上（CNAME → cdn.dnsv1.com），源站是 COS + 数据万象
 * （响应头 Server: tencent-cos / tencent-ci），本身就支持 imageMogr2 处理参数。
 * 实测效果：
 *   头像  1803KB/页 → 67KB/页   （96x96，约 1/27）
 *   配图  268KB/张  → 14.5KB/张 （360 最长边，约 1/18）
 *
 * ⚠️ 曾经的现象：CDN 缓存未命中时图片大面积裂图。
 *   实测（绕过系统代理、4 轮 × 15 张头像）：原图地址失败 0/60，带 imageMogr2 参数
 *   失败 12/60；全新派生图地址（必然 cache miss）甚至 60/60 全失败，浏览器报
 *   net::ERR_BLOCKED_BY_ORB / ERR_EMPTY_RESPONSE / ERR_CONTENT_LENGTH_MISMATCH，
 *   而完全相同的地址用 curl / Node 请求 100% 成功、字节数与 Content-Length 也一致。
 *
 *   ✅ 根因已定位，跟 imageMogr2 无关，是 **COS 存储桶的防盗链（Referer 白名单）**：
 *      白名单只放行 *.ylinhua.com 和「空 Referer」。其它来源（localhost、127.0.0.1、
 *      局域网 IP、不带 www 的 ylinhua.com）回源时被拒，响应是
 *        HTTP 403 / Content-Type: application/xml
 *        <Error><Code>AccessDenied</Code>
 *               <Message>You are denied by bucket referer rule</Message></Error>
 *      <img> 拿到 XML 而不是图片，被 Chrome 的 ORB 拦掉 → ERR_BLOCKED_BY_ORB。
 *
 *      几个当初把人绕进去的点，记下来免得再踩：
 *      · 校验发生在**存储桶**、不是 CDN。命中边缘缓存时 CDN 不回源、不校验 Referer，
 *        所以「热」的图好好的，「冷」的图必裂 —— 于是越少见的派生尺寸失败率越高，
 *        全新尺寸 100% 失败。看起来像 imageMogr2 不稳，其实原图 cache miss 一样 403。
 *      · curl / Node 默认不带 Referer，正好命中「空 Referer」放行，所以怎么测都是 200。
 *        复现必须带上 Referer：curl -H 'Referer: http://localhost/' <url>
 *      · 同理，把地址单独粘到浏览器地址栏也是好的（地址栏访问不带 Referer）。
 *      · 403 不会被边缘缓存，所以不存在「开发环境把线上图片污染了」的问题。
 *
 *   现在前端已经统一不带 Referer（index.html 的 <meta name="referrer" content="same-origin">
 *   ＋ <smart-img> 上的 referrerpolicy="no-referrer"），这条链路不再因为来源被拒。
 *   但**调用方仍然必须有失败降级**（图片本身可能 404、域名可能失效）。
 *   统一用 <smart-img> 组件（src/components/smart-img/index.vue），它内置
 *   「缩略图 → 原图 → 本地占位图」三级降级。不要直接把这里的返回值塞给裸 <img>。
 *
 *   同理，派生尺寸种类要尽量少 —— 每多一种尺寸就多一份冷启动失败面。所以这里只
 *   开放固定档位（见 SIZE_LADDER），不接受任意尺寸。
 */

import { IMG_CDN_HOST, BLOCKED_IMAGE_KEY } from '@/constant/cdn'

/**
 * 头像**唯一**的派生尺寸。全站头像都用这一个，不要按位置分档。
 *
 * 原因（实测）：很少被请求的派生尺寸会被 CDN 边缘缓存淘汰，于是每次打开都要重新
 * 现场处理一遍。对同一批头像：
 *   96 档位（信息流、评论、会话列表都在用）  8/8 命中缓存，平均 78ms，Age 已 33000 秒
 *   192 档位（曾只有个人中心一处在用）      0/8 命中缓存，每张 296~455ms；
 *                                          预热 9 分钟后再测，又有一半变回 Cache Miss
 * 也就是说「给大头像单独开一个更大的档位」= 那个档位永远是冷的，
 * 表现就是个人中心头像每次打开都很慢（上一版就踩了这个坑）。
 *
 * 代价：个人主页头像盒子是 90px（移动端 64~72px），96 的源图在 2x 屏上会略软。
 * 想要更清晰的正确做法是让后端在上传头像时就额外存一份小图，
 * 而不是在前端再加一个派生档位。
 */
export const AVATAR_SIZE = 96
/** 列表配图（桌面 138px 九宫格、移动三列）默认最长边 */
export const CONTENT_SIZE = 360

/**
 * 允许的派生尺寸档位。
 * 固定档位的目的是让全站所有用户共用同一批 URL：只要有人请求过一次，
 * 之后 10 天内（响应头 Cache-Control: max-age=864000）都是 CDN 缓存命中。
 * 档位越少越好，理由见上面 AVATAR_SIZE 的注释。
 */
const SIZE_LADDER = [96, 192, 360, 720, 1200]

function snapSize (size) {
  const n = Number(size)
  if (!n || n <= 0) return AVATAR_SIZE
  for (let i = 0; i < SIZE_LADDER.length; i++) {
    if (SIZE_LADDER[i] >= n) return SIZE_LADDER[i]
  }
  return SIZE_LADDER[SIZE_LADDER.length - 1]
}

/**
 * 默认头像：内联 SVG，零请求、永不失效。
 *
 * 原来的兜底图指向 http://img.shiyit.com/...png —— 该域名现在 443 拒绝连接
 * （curl 退出码 7），也就是说「头像为空」「头像 404」兜底后依然是裂图，
 * 还要先白等一次连接失败。改成 data URI 后不再依赖任何外部域名。
 */
export const DEFAULT_AVATAR_URI =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40">' +
    '<circle cx="20" cy="20" r="20" fill="#e8eaed"/>' +
    '<circle cx="20" cy="15.5" r="6.2" fill="#bcc1c7"/>' +
    '<path d="M6.9 34.4a13.6 13.6 0 0 1 26.2 0A19.96 19.96 0 0 1 20 40a19.96 19.96 0 0 1-13.1-5.6z" fill="#bcc1c7"/>' +
    '</svg>'
  )

/** 内容图加载失败时的占位图（灰底 + 山水图标），同样是内联 SVG */
export const DEFAULT_IMAGE_URI =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
    '<rect width="64" height="64" fill="#f2f3f5"/>' +
    '<circle cx="23" cy="22" r="5" fill="#d0d4d9"/>' +
    '<path d="M10 46l13-15 9 10 7-7 15 12z" fill="#d0d4d9"/>' +
    '</svg>'
  )

/** 两张状态图标共用的图片框（灰底 + 相框 + 太阳 + 山），和 App 端 static/images 下的同款 */
const STATE_FRAME =
  '<rect width="64" height="64" fill="#f2f3f5"/>' +
  '<rect x="17" y="21" width="30" height="24" rx="3" fill="none" stroke="#c0c4cc" stroke-width="2"/>' +
  '<circle cx="25.5" cy="28.5" r="2.5" fill="#c0c4cc"/>' +
  '<path d="M19.5 43l9-10 5 5.5 3.5-3.5 8.5 8z" fill="#c0c4cc"/>'

/**
 * 被内容审核屏蔽的图：带斜杠的图片图标，不带字，内联 SVG。
 * 聊天气泡里的文案按收发方向不同，由 components/chat-image-state 自己写；引用块那种小缩略图里只放图标。
 * 服务端会把被冻结的聊天图片地址换成 BLOCKED_IMAGE_KEY，渲染时换成这张。
 */
export const BLOCKED_IMAGE_URI =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' + STATE_FRAME +
    '<path d="M14 18l36 30" stroke="#f2f3f5" stroke-width="6"/>' +
    '<path d="M14 18l36 30" stroke="#c0c4cc" stroke-width="2" stroke-linecap="round"/>' +
    '</svg>'
  )

/** 加载失败（不是审核屏蔽）的破图图标：相框中间裂开一道，配「点击重试」 */
export const BROKEN_IMAGE_URI =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' + STATE_FRAME +
    '<path d="M36 19l-3 6 4 5-3 6 4 5-2 5" fill="none" stroke="#f2f3f5" stroke-width="3.5" stroke-linejoin="round"/>' +
    '</svg>'
  )

/**
 * 是不是被内容审核屏蔽的图（服务端换上的占位图地址，http / https、带不带参数都算）。
 * 这种图不要打开预览，它本身只是一张提示图。
 */
export function isBlockedImageUrl (url) {
  if (typeof url !== 'string' || !url) return false
  if (url === BLOCKED_IMAGE_URI) return true
  const src = url.trim()
  if (!/^https?:\/\//i.test(src)) return false
  try {
    const parsed = new URL(src)
    return parsed.hostname === IMG_CDN_HOST && parsed.pathname === '/' + BLOCKED_IMAGE_KEY
  } catch (e) {
    return false
  }
}

/**
 * 支持 imageMogr2 的图片域名白名单。
 * 从 VUE_APP_IMG_API 推导，dev / prod 换域名不用改代码；解析失败时回落到线上域名。
 *
 * 注意：img.shiyit.com、img.yqingke.com 这两个历史域名已经彻底失效
 * （前者 443 拒绝连接，后者 DNS 都解析不到），不在白名单里，也不该再被引用。
 */
const CI_HOSTS = (() => {
  // 主机名取自 @/constant/cdn，不在这里写字面量（那个文件不 import 任何东西，无循环依赖）
  const hosts = [IMG_CDN_HOST]
  const base = process.env.VUE_APP_IMG_API
  if (base) {
    try {
      const host = new URL(base).hostname
      if (host && hosts.indexOf(host) === -1) hosts.push(host)
    } catch (e) {
      // 环境变量配错时静默忽略，仅使用默认域名
    }
  }
  return hosts
})()

/** 该地址是否能加 imageMogr2 参数 */
function canProcess (url) {
  if (typeof url !== 'string' || !url) return false
  // data: / blob: 是本地资源（上传预览），相对路径是打包进产物的静态图，都不走 CDN
  if (/^(data|blob):/i.test(url)) return false
  if (!/^https?:\/\//i.test(url)) return false
  // 已有 query 的可能是带签名的临时链接，追加参数会让签名校验失败
  if (url.indexOf('?') !== -1) return false
  try {
    return CI_HOSTS.indexOf(new URL(url).hostname) !== -1
  } catch (e) {
    return false
  }
}

/**
 * 头像地址：正方形缩略图（短边优先缩放 + 居中裁剪，结果是精确的 px × px）。
 *
 * 为什么不用 `thumbnail/96x96`：那是「等比缩放到 96x96 矩形内」，非正方形原图的短边
 * 会小于 96，配合 CSS 的 object-fit: cover 反而要被放大、变虚。
 * `thumbnail/!96x96r` 是短边优先，再 crop 居中裁成正方形，和 cover 表现完全一致。
 *
 * @param {string} url 接口返回的头像地址
 * @param {number} [px=AVATAR_SIZE] 目标边长，会向上对齐到档位
 * @returns {string} 处理后的地址；不支持处理时原样返回；空值返回默认头像
 */
export function buildAvatarUrl (url, px = AVATAR_SIZE) {
  if (typeof url !== 'string' || !url.trim()) return DEFAULT_AVATAR_URI
  const src = url.trim()
  if (!canProcess(src)) return src
  const n = snapSize(px)
  return `${src}?imageMogr2/thumbnail/!${n}x${n}r/crop/${n}x${n}/gravity/center`
}

/**
 * 内容图地址：限制最长边，等比缩放，**只缩不放**。
 *
 * 末尾的 `>`（URL 编码成 %3E）是「只在原图更大时才缩」。实测很关键：
 *   不加 `>`：144x52 的小图会被放大成 360x130，1050B → 3874B（反而变大 3.7 倍）
 *   加了 `>`：小图原样返回 1050B，大图 268873B → 14581B
 * 这里显式写 %3E 而不是裸 `>`，是为了让生成的 URL 字符串稳定、CDN 缓存键一致。
 *
 * ⚠️ 全屏预览（preview-src-list）必须用原图，不要传这个函数的结果。
 * ⚠️ 不要用在 object-fit: cover 裁切显示的格子里（帖子九宫格、评论配图），
 *    长图会被压成细条再放大，用下面的 buildCoverUrl。
 *
 * @param {string} url 原图地址
 * @param {number} [maxSide=CONTENT_SIZE] 最长边上限，会向上对齐到档位
 */
export function buildContentUrl (url, maxSide = CONTENT_SIZE) {
  if (typeof url !== 'string' || !url.trim()) return ''
  const src = url.trim()
  if (!canProcess(src)) return src
  const n = snapSize(maxSide)
  return `${src}?imageMogr2/thumbnail/${n}x${n}%3E`
}

/**
 * 正方形格子（object-fit: cover）里显示的内容图：短边优先缩放 + 居中裁成 n x n。
 * 就是各家 CDN 通用的「fill」缩略图（七牛 imageView2/1、阿里云 m_fill），和头像是同一条规则。
 *
 * 为什么帖子配图不能用 buildContentUrl（限最长边）：格子是 cover 裁切显示的，
 * 决定清晰度的是**短边**。长图的最长边是高度，限最长边等于把宽度压成细条 ——
 * 实测线上 572x1920 的长图走 360 档只剩 107x360，塞进 138px（2x 屏 276 物理像素）的格子
 * 要放大 2.6 倍，图越长越糊。
 *
 * 规则：`thumbnail/!{n}x{n}r` 让短边等于 n，再 `crop/{n}x{n}/gravity/center` 居中裁成正方形。
 * 正方形格子 cover 显示出来的正好就是这块，画面和拿整张图 cover 完全一样，体积最小。
 * 实测：572x1920 长图 360 档 → 360x360 12.5KB；1080x1347 → 360x360 29KB；
 * 长图、宽图、比格子还小的图，输出都稳定是 n x n。
 *
 * ⚠️ 只适用于正方形格子。非正方形格子（比如 16:9 封面）用这个会多裁掉两边。
 * ⚠️ 两步之间必须用管道 `|`（%7C）分开，不能像头像那样写在同一个 imageMogr2 里。
 *    实测写在一起时，只要需要放大（原图短边 < n）或者是宽图，数据万象就会先裁原图再缩放，
 *    出来的不是正方形：720 档下 572x1920 → 720x906，2000x500 → 1037x720。
 *
 * 代价：短边优先（`!...r`）不支持「只缩不放」（加 `>` 实测直接返回原图），
 * 短边小于 n 的小图会被放大到 n，只多几 KB，和头像规则是同一个取舍。
 *
 * ⚠️ 全屏预览（preview-src-list）同样必须用原图。
 *
 * @param {string} url 原图地址
 * @param {number} [size=CONTENT_SIZE] 格子边长（物理像素），会向上对齐到档位
 */
export function buildCoverUrl (url, size = CONTENT_SIZE) {
  if (typeof url !== 'string' || !url.trim()) return ''
  const src = url.trim()
  if (!canProcess(src)) return src
  const n = snapSize(size)
  return `${src}?imageMogr2/thumbnail/!${n}x${n}r%7CimageMogr2/crop/${n}x${n}/gravity/center`
}

/**
 * 按用途取地址。<smart-img> 内部用，也方便别处直接调用。
 * @param {string} url
 * @param {'avatar'|'content'|'cover'|'raw'} mode
 * @param {number} size
 */
export function buildImageUrl (url, mode, size) {
  // 被屏蔽的图一律画内联占位图：零请求，CDN 上那一份没上传、被刷掉时也不会裂
  if (isBlockedImageUrl(url)) return BLOCKED_IMAGE_URI
  if (mode === 'avatar') return buildAvatarUrl(url, size)
  if (mode === 'content') return buildContentUrl(url, size)
  if (mode === 'cover') return buildCoverUrl(url, size)
  return typeof url === 'string' ? url.trim() : ''
}

/** 对应用途的本地占位图 */
export function placeholderFor (mode) {
  return mode === 'avatar' ? DEFAULT_AVATAR_URI : DEFAULT_IMAGE_URI
}
